// Fetch helpers for the arena's public JSON API (same origin). Shapes: src/api/contract.ts.

import type {
  AdminStatusResponse,
  HeadToHeadResponse,
  LeaderboardResponse,
  MatchDetailResponse,
  MatchListResponse,
  ModeFilter,
  ModelProfileResponse,
  ModelsResponse,
  PromptBundleResponse,
  SetupFilter,
  StatusResponse,
} from '../src/api/contract';
import type { Side } from '../src/types';

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export function isNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

export function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

function sentence(text: string): string {
  const t = text.trim();
  if (!t) return '';
  return /[.!?…]$/.test(t) ? t : `${t}.`;
}

/** A readable sentence for any error thrown while loading data. */
export function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 0) return sentence(err.message);
    if (err.status === 404) return `Not found: ${sentence(err.message)}`;
    if (err.status === 429) return 'The server is busy right now. Please try again in a minute.';
    if (err.status >= 500) return `The arena's server had a problem (HTTP ${err.status}): ${sentence(err.message)}`;
    return `${sentence(err.message)} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return sentence(err.message) || 'Unknown error.';
  return 'Unknown error.';
}

const TIMEOUT_MS = 20_000;

async function getJSON<T>(path: string, signal?: AbortSignal): Promise<T> {
  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  const onAbort = (): void => ctrl.abort();
  signal?.addEventListener('abort', onAbort);
  let res: Response;
  try {
    res = await fetch(path, { headers: { accept: 'application/json' }, signal: ctrl.signal, credentials: 'same-origin' });
  } catch (err) {
    if (signal?.aborted) throw err;
    if (ctrl.signal.aborted) throw new ApiError(0, 'The server took too long to answer.');
    throw new ApiError(0, 'Could not reach the server. Check your connection.');
  } finally {
    window.clearTimeout(timer);
    signal?.removeEventListener('abort', onAbort);
  }
  if (!res.ok) {
    let message = res.statusText || 'Request failed.';
    try {
      const body: unknown = await res.json();
      if (body && typeof body === 'object' && 'error' in body && typeof body.error === 'string') message = body.error;
    } catch {
      // Not JSON: keep the status text.
    }
    throw new ApiError(res.status, message);
  }
  try {
    return (await res.json()) as T;
  } catch {
    throw new ApiError(res.status, 'The server sent a response this page could not read.');
  }
}

function query(params: Record<string, string | number | null | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== null && v !== undefined && v !== '') qs.set(k, String(v));
  const s = qs.toString();
  return s ? `?${s}` : '';
}

// ---------------------------------------------------------------------------
// Page URLs (always encode ids: model ids contain "/" and ":")

export const matchUrl = (id: string): string => `/match${query({ id })}`;
export const modelUrl = (id: string): string => `/model${query({ id })}`;
export const h2hUrl = (a: string, b: string): string => `/h2h${query({ a, b })}`;
/** A prompt version's full text, as recorded (JSON). */
export const promptUrl = (id: string): string => `/api/prompts/${encodeURIComponent(id)}`;
export const rawUrl = (matchId: string, seq: number): string =>
  `/api/matches/${encodeURIComponent(matchId)}/raw/${encodeURIComponent(String(seq))}`;

// ---------------------------------------------------------------------------
// Endpoints

export interface LeaderboardParams {
  role: Side;
  mode: ModeFilter;
  setup: SetupFilter;
}

export const api = {
  leaderboard: (p: LeaderboardParams, signal?: AbortSignal) =>
    getJSON<LeaderboardResponse>(`/api/leaderboard${query({ role: p.role, mode: p.mode, setup: p.setup })}`, signal),

  /** `role` narrows `model` to one side; leave it out for matches in either role. `opponent` keeps one pairing. */
  matches: (
    p: { model?: string; role?: Side; opponent?: string; mode?: ModeFilter; setup?: SetupFilter; before?: string | null; limit?: number },
    signal?: AbortSignal,
  ) =>
    getJSON<MatchListResponse>(
      `/api/matches${query({ model: p.model, role: p.role, opponent: p.opponent, mode: p.mode, setup: p.setup, before: p.before, limit: p.limit })}`,
      signal,
    ),

  match: (id: string, signal?: AbortSignal) => getJSON<MatchDetailResponse>(`/api/matches/${encodeURIComponent(id)}`, signal),

  models: (signal?: AbortSignal) => getJSON<ModelsResponse>('/api/models', signal),

  model: (id: string, signal?: AbortSignal) => getJSON<ModelProfileResponse>(`/api/models/${encodeURIComponent(id)}`, signal),

  h2h: (a: string, b: string, signal?: AbortSignal) => getJSON<HeadToHeadResponse>(`/api/h2h${query({ a, b })}`, signal),

  status: (signal?: AbortSignal) => getJSON<StatusResponse>('/api/status', signal),

  prompts: (signal?: AbortSignal) => getJSON<PromptBundleResponse>('/api/prompts/current', signal),

  /** Behind Cloudflare Access: answers only a browser signed in as an admin. */
  adminStatus: (signal?: AbortSignal) => getJSON<AdminStatusResponse>('/admin/api/status', signal),
};

